import {
  AuditingDrawerContent,
  AuditingPanelProps,
} from "features/auditing/AuditingPanel";
import { AuditingTable } from "features/auditing/AuditingTable";
import {
  GetLogs,
  useAuditTablePaginate,
} from "features/auditing/useAuditTablePaginate";
import {
  internalAuditLogTableRetrieve,
  useInternalAuditLogFilterDataRetrieve,
  useInternalModelAuditLogEntriesList,
} from "gen/endpoints/internal/internal";
import { AuditLogTableQueryDataRequest } from "gen/model/auditLogTableQueryDataRequest";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBoxPanel } from "shared/components/table/box/TableBoxPanel";

const InternalAuditingPanel: React.FC<AuditingPanelProps> = ({
  onClose,
  apiAuditLogEntryId,
  getRow,
}) => {
  const { data: modelAuditLogEntryList } =
    useInternalModelAuditLogEntriesList(apiAuditLogEntryId);

  return (
    <TableBoxPanel onClose={onClose}>
      <QueryBoundary>
        <AuditingDrawerContent
          onClose={onClose}
          apiAuditLogEntryId={apiAuditLogEntryId}
          getRow={getRow}
          modelAuditLogEntryList={modelAuditLogEntryList}
        />
      </QueryBoundary>
    </TableBoxPanel>
  );
};

export const InternalAuditingPage: React.FC = () => {
  const { t } = useTranslation(["common", "auditing", "enums"]);

  const [filters, setFilters] = useState<AuditLogTableQueryDataRequest>({});

  const { data: auditLogFilterData } = useInternalAuditLogFilterDataRetrieve();

  const getLogs: GetLogs = useCallback(
    async ({ startRow, endRow, isColumnsOnly = false }) =>
      internalAuditLogTableRetrieve({
        start_date: filters.start_date ?? undefined,
        end_date: filters.end_date ?? undefined,
        user_ids: filters.user_ids ?? [],
        operation_ids: filters.operation_ids ?? [],
        audit_action_types: filters.audit_action_types ?? [],
        fund_family_ids: filters.fund_family_ids ?? [],
        start_row: startRow,
        end_row: endRow,
        columns_only: isColumnsOnly,
      }),
    [filters],
  );

  const { tableData, serverSideDatasource } = useAuditTablePaginate({
    getLogs,
    queryKey: "internal-auditing",
    filters,
  });

  return (
    <PageContent isFullHeight={true}>
      <PageHeader title={t("auditing:audit")} size="lg" />
      <PageBody>
        <AuditingTable
          tableData={tableData}
          serverSideDatasource={serverSideDatasource}
          auditLogFilterData={auditLogFilterData}
          auditingPanel={InternalAuditingPanel}
          filters={filters}
          setFilters={setFilters}
        />
      </PageBody>
    </PageContent>
  );
};
